import { MfaPolicy, type OrganizationMinimal } from '@piccolohealth/pbs-common';
import { ChakraV3, ScrollArea, type SelectOption, Spin, useOptionsFocus } from '@piccolohealth/ui';
import { matchSorter, P } from '@piccolohealth/util';
import keyboardJS from 'keyboardjs';
import React from 'react';
import { FaArrowRight, FaLock, FaSearch } from 'react-icons/fa';
import { TbLayoutSidebarLeftCollapse } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { createModal, showModal } from '../../components/generic/Modal';
import { PiccoloIcon } from '../../components/generic/PiccoloIcon';
import { useOrganizationsQuery } from '../../graphql/hooks/useOrganizationsQuery';
import { useAppContext } from '../../hooks/useAppContext';

interface OrganizationItemProps {
  name: string;
  logo?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDefault?: boolean;
  mfaPolicy: MfaPolicy;
}

const OrganizationItem = (props: OrganizationItemProps) => {
  const { name, logo, isSelected, isDisabled, isDefault, mfaPolicy } = props;

  return (
    <ChakraV3.HStack gap={4} w='full'>
      <ChakraV3.Avatar
        w='42px'
        h='42px'
        p='2px'
        color='purple.600'
        fontWeight='600'
        name={name}
        bg='transparent'
        src={logo}
        opacity={isDisabled ? 0.5 : 1}
        shape='rounded'
      />
      <ChakraV3.Text
        fontWeight='semibold'
        fontSize='md'
        whiteSpace='normal'
        color='gray.700'
        aria-selected={isSelected}
        opacity={isDisabled ? 0.5 : 1}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
      >
        {name}
      </ChakraV3.Text>
      <ChakraV3.Spacer />
      <ChakraV3.HStack gap={2}>
        {isDefault && (
          <ChakraV3.Tag size='sm' colorPalette='green' variant='subtle'>
            Default
          </ChakraV3.Tag>
        )}
        {mfaPolicy === MfaPolicy.Required && (
          <ChakraV3.Tag size='sm' colorPalette='blue' variant='subtle'>
            <ChakraV3.Icon mr={1}>
              <FaLock />
            </ChakraV3.Icon>
            MFA
          </ChakraV3.Tag>
        )}
      </ChakraV3.HStack>
      <ChakraV3.Stack w='8' pr={4}>
        {isSelected && (
          <ChakraV3.Icon color='purple.600'>
            <FaArrowRight />
          </ChakraV3.Icon>
        )}
      </ChakraV3.Stack>
    </ChakraV3.HStack>
  );
};

interface OrganizationChooserModalProps {
  userHasMfa: boolean;
  defaultOrganizationId?: string;
}

export const OrganizationChooserModal = createModal<OrganizationChooserModalProps>((props) => {
  const { modal, userHasMfa, defaultOrganizationId } = props;

  const [query, setQuery] = React.useState('');
  const focusedRef = React.useRef<HTMLLIElement>(null);

  const navigate = useNavigate();

  const { data, isLoading } = useOrganizationsQuery({});

  const organizations: SelectOption<OrganizationMinimal>[] = React.useMemo(() => {
    return (data?.organizations ?? []).map((organization) => ({
      label: organization.name,
      value: organization.id,
      raw: organization as OrganizationMinimal,
    }));
  }, [data?.organizations]);

  const filteredOrganizations = React.useMemo(() => {
    if (query.length < 2) {
      return organizations;
    }

    return matchSorter(organizations, query, {
      keys: ['label'],
    });
  }, [query, organizations]);

  const { focusedOption, focusFirstOption, focusPrevOption, focusNextOption, focusOption } =
    useOptionsFocus<OrganizationMinimal>(filteredOrganizations);

  const navigateToOrganization = React.useCallback(
    (organization: OrganizationMinimal) => {
      if (organization.mfaPolicy === MfaPolicy.Required && !userHasMfa) {
        return;
      }

      modal.hide();
      void navigate(`/organizations/${organization.id}`);
    },
    [navigate, modal, userHasMfa],
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();

          focusNextOption();
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          focusPrevOption();
          break;
        }
        case 'Enter': {
          if (filteredOrganizations.length <= 0 || !focusedOption) {
            break;
          }

          navigateToOrganization(focusedOption.option.raw);
          break;
        }
      }
    },
    [
      filteredOrganizations,
      focusedOption,
      focusNextOption,
      focusPrevOption,
      navigateToOrganization,
    ],
  );

  React.useLayoutEffect(() => {
    focusFirstOption();
  }, [focusFirstOption]);

  React.useLayoutEffect(() => {
    if (focusedOption?.keyboard) {
      focusedRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
      });
    }
  }, [focusedOption]);

  const content = P.run(() => {
    if (isLoading) {
      return (
        <ChakraV3.Stack h='full'>
          <Spin />
        </ChakraV3.Stack>
      );
    }

    if (P.isEmpty(filteredOrganizations)) {
      return null;
    }

    return (
      <ScrollArea maxH='md' overflow='auto' py={2}>
        <ChakraV3.List.Root px={4} data-pw='organizationChooserMenu' variant='plain'>
          {filteredOrganizations.map((organization) => {
            const isFocused = focusedOption?.option.value === organization.value;
            const ref = isFocused ? focusedRef : undefined;
            const isDefault = organization.raw.id === defaultOrganizationId;
            const isDisabled = organization.raw.mfaPolicy === MfaPolicy.Required && !userHasMfa;

            return (
              <ChakraV3.List.Item
                ref={ref}
                key={organization.value}
                bg='white'
                rounded='lg'
                px={2}
                py={1}
                my={2}
                data-pw={`organizationChooserItem-${organization.raw.id}`}
                aria-selected={isFocused ? true : undefined}
                onMouseEnter={() => focusOption(organization, false)}
                onClick={() => navigateToOrganization(organization.raw)}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
              >
                <OrganizationItem
                  name={organization.raw.name}
                  logo={organization.raw.logo ?? undefined}
                  isSelected={isFocused}
                  isDisabled={isDisabled}
                  isDefault={isDefault}
                  mfaPolicy={organization.raw.mfaPolicy}
                />
              </ChakraV3.List.Item>
            );
          })}
        </ChakraV3.List.Root>
      </ScrollArea>
    );
  });

  return (
    <ChakraV3.Dialog.Root
      open={modal.visible}
      onOpenChange={modal.hide}
      onExitComplete={modal.remove}
      size='lg'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.Dialog.Header p={0}>
            <ChakraV3.HStack py={2} px={6} w='full'>
              <ChakraV3.Icon size='md' color='gray.300'>
                <FaSearch />
              </ChakraV3.Icon>
              <ChakraV3.Input
                onChange={(e) => setQuery(e.target.value)}
                size='2xl'
                border='none'
                outline='none'
                fontWeight='semibold'
                placeholder='Switch organization'
                onKeyDown={onKeyDown}
                autoComplete='off'
                data-1p-ignore
              />

              <ChakraV3.HStack pointerEvents='none' fontSize='sm' color='gray.400' flexShrink={0}>
                <ChakraV3.Kbd size='sm' color='secondary'>
                  Cmd/Ctrl
                </ChakraV3.Kbd>{' '}
                +{' '}
                <ChakraV3.Kbd size='sm' color='secondary'>
                  K
                </ChakraV3.Kbd>
              </ChakraV3.HStack>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Header>
          <ChakraV3.Dialog.Body p={0}>
            <ChakraV3.Stack h='md'>{content}</ChakraV3.Stack>
          </ChakraV3.Dialog.Body>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});

export interface OrganizationChooserButtonProps {
  onShrink: () => void;
}

export const OrganizationChooserButton = (props: OrganizationChooserButtonProps) => {
  const { onShrink } = props;
  const { organization, user } = useAppContext();

  const hasMultipleOrgs = user.organizationMemberships.length > 1;
  const userHasMfa = !P.isUndefined(user.mfaEnrolment);

  const onClick = React.useCallback(() => {
    if (hasMultipleOrgs) {
      showModal(OrganizationChooserModal, {
        defaultOrganizationId: organization.id,
        userHasMfa,
      });
    }
  }, [hasMultipleOrgs, organization.id, userHasMfa]);

  React.useEffect(() => {
    const keys = ['super + k', 'ctrl + k'];
    keyboardJS.bind(keys, onClick);

    return () => {
      keyboardJS.unbind(keys, onClick);
    };
  }, [onClick]);

  return (
    <ChakraV3.HStack w='full' gap={0} py={4} px={4} align='center'>
      <ChakraV3.Box w='full'>
        <ChakraV3.Button onClick={onClick} variant='ghost' w='full' h='fit-content' py={0.5} px={1}>
          <ChakraV3.HStack w='full'>
            {organization.logo ? (
              <ChakraV3.Avatar
                src={organization.logo ?? undefined}
                w='42px'
                h='42px'
                p='2px'
                color='purple.600'
                borderRadius='6px'
                fontWeight='bold'
                name={organization.name}
                bg='transparent'
                onClick={onClick}
              />
            ) : (
              <PiccoloIcon boxSize='6' color='purple.500' />
            )}
            <ChakraV3.Text fontSize={14} fontWeight='bold' whiteSpace='pre-wrap' textAlign='left'>
              {organization.name}
            </ChakraV3.Text>
          </ChakraV3.HStack>
        </ChakraV3.Button>
      </ChakraV3.Box>
      <ChakraV3.Spacer />
      <ChakraV3.IconButton
        size='md'
        variant='ghost'
        aria-label='sidebar-button'
        color='gray.600'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onShrink();
        }}
      >
        <TbLayoutSidebarLeftCollapse size='20px' />
      </ChakraV3.IconButton>
    </ChakraV3.HStack>
  );
};
